/** Module 'costs': usage, money, budgets (docs/product/CostTrackingMobileControl.md sections 3 to 5). */
import type { Id, Ts } from '../index.ts';
import type { AccountBucket, ApiKeySplit, SpendAccount } from './accounts.ts';

/**
 * Where a cost figure came from. `transcript`: tokens read from Claude Code transcripts,
 * costed at API list price. `notional`: the same, for subscription seats (Pro, Max), which
 * have no per-token invoice, so the money is what the tokens would cost on the API.
 */
export type CostSource = 'telemetry' | 'transcript' | 'billing' | 'notional';

/** How this org pays for Claude Code. Subscription seats make every cost notional. */
export type BillingMode = 'subscription' | 'api';

/** Token counts of one model in one session. `input` is uncached input only. */
export interface TokenCounts {
  input: number;
  output: number;
  cacheRead: number;
  /** Every cache write (5 minute and 1 hour). */
  cacheWrite: number;
  /** The part of cacheWrite that went to the 1 hour cache (priced higher). */
  cacheWrite1h: number;
  /** API calls (distinct message ids). */
  calls: number;
}

/**
 * Price variants are kept apart in usage keys: `<model>` for standard calls, `<model>#fast`
 * for fast mode, `<model>#long` for calls whose prompt exceeded LONG_PROMPT_TOKENS (models
 * priced by prompt length, such as Claude Haiku 5.5).
 */
export type PriceVariant = 'fast' | 'long';
export const LONG_PROMPT_TOKENS = 100_000;

/**
 * A session's tokens as its transcripts tell them: the main transcript plus every subagent
 * transcript, per model (and price variant), each API message counted once by its id.
 * Sent by the agent on `Session.usage`.
 */
export interface SessionTokenUsage {
  byModel: Record<string, TokenCounts>;
  /** Part of a transcript was not read (a large file read from its tail): counts are low. */
  partial: boolean;
  /**
   * The same counts by the day each API message was written ('YYYY-MM-DD', the agent's local
   * time, see localDayKey), then by model key. A message with no timestamp is in byModel only.
   * Optional: older agents omit it, and the hub then spreads history over the session's span.
   */
  byDay?: Record<string, Record<string, TokenCounts>>;
}

/** 'YYYY-MM-DD' of a timestamp in local time: the day key of usage rows and SessionTokenUsage.byDay. */
export function localDayKey(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface PriceRates {
  /** USD per million tokens. */
  input: number;
  output: number;
  cacheRead: number;
  /** 5 minute cache write. */
  cacheWrite: number;
  /** 1 hour cache write. */
  cacheWrite1h: number;
}

export interface ModelPrice extends PriceRates {
  /** Model id prefix it applies to, e.g. 'claude-opus-5'. Longest match wins. */
  match: string;
  /** 'Opus 5.5'. */
  label: string;
  /** Fast mode rates (cache multipliers already applied). */
  fast?: PriceRates;
  /** Rates for a call whose prompt is over LONG_PROMPT_TOKENS, when the model is priced by prompt length. */
  long?: PriceRates;
}

export interface PricingTable {
  /** Bumped on every change; each usage row stores the version it was costed with. */
  version: string;
  effectiveFrom: Ts;
  /** Where the prices were taken from. */
  source: string;
  prices: ModelPrice[];
}

export interface UsageTotals {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  /** USD. */
  cost: number;
}

/**
 * One ledger row: tokens a session spent on one model on one day (UTC), costed once with
 * the pricing version current when they were seen. Rows are never re-costed.
 */
export interface UsageRow {
  /** 'YYYY-MM-DD', UTC. */
  day: string;
  sessionId: Id;
  machineId: Id;
  projectId: Id | null;
  missionId: Id | null;
  /** Model id plus price variant suffix, see PriceVariant. */
  model: string;
  tokens: TokenCounts;
  /** USD. */
  cost: number;
  pricingVersion: string;
  source: CostSource;
  /** True when no price matched the model: cost is 0 and the row shows as unpriced. */
  unpriced?: boolean;
  /** The account the session ran on (SessionAccount.key, ADR 0067); absent or null when unknown. */
  accountKey?: string | null;
}

/** One session's usage, per model. */
export interface SessionUsage {
  sessionId: Id;
  machineId: Id;
  projectId: Id | null;
  missionId: Id | null;
  title: string;
  byModel: Record<string, UsageTotals>;
  total: UsageTotals;
  source: CostSource;
  /** The pricing version of the newest row (older rows keep theirs). */
  pricingVersion: string;
  /** Tokens per minute over the last 10 minutes, for runaway detection. */
  rate: number;
  /** Counts cover only part of a transcript. */
  partial: boolean;
  /** Some of its tokens were spent on a model with no price (another vendor's): its cost leaves them out. */
  unpriced?: boolean;
  /** The account the session ran on (ADR 0067); null when unknown. Older hubs omit it. */
  accountKey?: string | null;
  updatedAt: Ts;
}

export type BudgetScope =
  | { kind: 'org' }
  | { kind: 'project'; projectId: Id }
  | { kind: 'session'; sessionId: Id }
  | { kind: 'mission'; missionId: Id };

export type BudgetPeriod = 'day' | 'month' | 'total';

export interface Budget {
  id: Id;
  scope: BudgetScope;
  period: BudgetPeriod;
  /** USD. */
  limit: number;
  /** Fraction of the limit that raises a warning (default 0.8). */
  softAt: number;
  /** At 100%: 'warn' only alerts; 'stop' denies further work in launched sessions. */
  onLimit: 'warn' | 'stop';
  createdAt: Ts;
  updatedAt: Ts;
}

/** POST /api/costs/budgets and PUT /api/costs/budgets/:id. */
export interface BudgetInput {
  scope: BudgetScope;
  period: BudgetPeriod;
  limit: number;
  softAt?: number;
  onLimit?: 'warn' | 'stop';
}

export interface BudgetStatus {
  budgetId: Id;
  /** 'Org this month', 'orgus this month'. */
  label: string;
  spent: number;
  limit: number;
  /** Period-end projection from the current burn rate (equals spent for 'total'). */
  forecast: number;
  state: 'ok' | 'soft' | 'hard';
  /** The forecast crosses the limit before the period ends. */
  forecastOver: boolean;
  periodStart: Ts;
  /** null for 'total'. */
  periodEnd: Ts | null;
}

export interface CostBucket {
  /** A project id, machine id, model key, day or mission id. '' for none. */
  key: string;
  label: string;
  cost: number;
  tokens: number;
  sessions: number;
}

export interface RunawayAlert {
  sessionId: Id;
  machineId: Id;
  projectId: Id | null;
  title: string;
  /** Tokens per minute now, and the session's own baseline. */
  rate: number;
  baseline: number;
  at: Ts;
  /** Still running hot. */
  active: boolean;
}

/** state.ext.costs */
export interface CostsState {
  pricing: PricingTable;
  billing: BillingMode;
  /** Sessions with usage this month, most expensive first (bounded). */
  sessions: SessionUsage[];
  budgets: Budget[];
  budgetStatus: BudgetStatus[];
  /** Spend today and this month across everything, USD. */
  today: number;
  month: number;
  /** Month-end projection from this month's burn rate. */
  forecastMonth: number;
  /** This month (UTC). */
  byProject: CostBucket[];
  byMachine: CostBucket[];
  byModel: CostBucket[];
  byMission: CostBucket[];
  /** The last 30 days, oldest first, every day present. */
  byDay: CostBucket[];
  /** This month by account (ADR 0067); key '' is sessions with no known account. Older hubs omit it. */
  byAccount?: AccountBucket[];
  /** Every account the hub has seen, newest first (ADR 0067). Older hubs omit it. */
  accounts?: SpendAccount[];
  /** Whether API-key spend is told apart from the sign-in (ADR 0067, amended 19 Oct 2026). Older hubs omit it. */
  apiKeys?: ApiKeySplit;
  /** Tokens this month by kind, for the cache hit ratio. */
  tokensMonth: TokenCounts;
  runaways: RunawayAlert[];
  /** Models seen without a price (cost counted as 0). */
  unpricedModels: string[];
  monthStart: Ts;
  monthEnd: Ts;
  updatedAt: Ts;
}

/** SSE 'ext' event types from the costs module. */
export type CostsEventType = 'state';

/** What other hub modules may ask the costs module: ctx.ext<CostsApi>('costs'). */
export interface CostsApi {
  /** Should a launched session be stopped from doing more work? */
  overBudget(sessionId: Id): { over: boolean; reason?: string };
  /** USD spent by a session so far (every row the hub has seen). */
  sessionCost(sessionId: Id): number;
  /** Ledger rows with fromDay <= day <= toDay ('YYYY-MM-DD'; toDay open when omitted), for module finance. */
  rows?(fromDay: string, toDay?: string): UsageRow[];
  /** The pricing table new usage is costed with, and how this org pays. */
  pricing?(): PricingTable;
  billing?(): BillingMode;
  /** Has any usage arrived for this session (state lists only the most expensive)? */
  hasUsage?(sessionId: Id): boolean;
  /** Every account the costs module has seen, with its display name (ADR 0067). */
  accounts?(): SpendAccount[];
  /**
   * Ledger rows from fromDay with API-key spend from the provider report carved out of the weak
   * sign-in rows (ADR 0067, amended 19 Oct 2026): a derived allocation, for by-account views
   * only. Totals equal rows(); `unmatched` and `movedOut` are USD by account key.
   */
  attributed?(fromDay: string, toDay?: string): AttributedRows;
}

/** CostsApi.attributed: rows as by-account views count them. */
export interface AttributedRows {
  rows: UsageRow[];
  /** Report usage of an API key no session could hold, by account key. */
  unmatched: Record<string, number>;
  /** USD moved out of an account's rows to API keys, by account key ('' the unknown account). */
  movedOut: Record<string, number>;
}

/**
 * Money for people: '$1,284', '$42.10', '$0.42', '$0.004', '<$0.001', '$0'. Always the
 * currency sign; cents below $1,000, whole dollars above, three decimals under a cent.
 */
export function formatUsd(usd: number): string {
  if (!Number.isFinite(usd) || usd === 0) return '$0';
  const neg = usd < 0 ? '-' : '';
  const v = Math.abs(usd);
  if (v >= 1000) return `${neg}$${Math.round(v).toLocaleString('en-US')}`;
  if (v >= 0.01) return `${neg}$${v.toFixed(2)}`;
  if (v >= 0.0005) return `${neg}$${v.toFixed(3)}`;
  return `${neg}<$0.001`;
}
